import { formatDate } from '../utils/dateConverter';
import React, { useCallback, useEffect, useState } from 'react';
import useLiveRefresh from '../utils/useLiveRefresh';
import { useNavigate } from 'react-router-dom';
import { approveHoliday, getHolidayEntries, rejectHoliday } from '../services/HolidayService';
import { approveFlexDay, getFlexDayEntries, rejectFlexDay } from '../services/FlexDayService';
import { approveOvertimeApproval, getOvertimeApprovals, rejectOvertimeApproval } from '../services/OvertimeApprovalService';
import { getFollowUps } from '../services/AcquisitionService';
import { useConfirm, usePrompt, useToast } from '../context/UIContext';
import '../styles/EmployeeArea.css';
import '../styles/OpenTasksModal.css';

const callNote = (item) => {
    if (item.bucket === 'overdue') {
        const days = item.days_overdue ?? 0;
        return `${days} Tag${days === 1 ? '' : 'e'} überfällig`;
    }
    return item.bucket === 'today' ? 'heute fällig' : 'kein Termin gesetzt';
};

export default function OpenTasksModal({ isOpen, onClose, onCountChange }) {
    const toast = useToast();
    const confirm = useConfirm();
    const prompt = usePrompt();
    const navigate = useNavigate();
    const [pendingItems, setPendingItems] = useState([]);
    const [callItems, setCallItems] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [holidays, flexDays, overtimes, followUps] = await Promise.allSettled([
                getHolidayEntries({ role: 'admin', userId: null }),
                getFlexDayEntries({ role: 'admin', userId: null }),
                getOvertimeApprovals({ role: 'admin', userId: null }),
                getFollowUps({ scope: 'mine' }),
            ]);
            const calls = (followUps.status === 'fulfilled' ? followUps.value.items : []) || [];
            setCallItems(calls);
            const pendingHolidays = (holidays.status === 'fulfilled' ? holidays.value : [])
                .filter(e => e.approvalStatus === 'pending')
                .map(e => ({ ...e, _kind: 'holiday' }));
            const pendingFlexDays = (flexDays.status === 'fulfilled' ? flexDays.value : [])
                .filter(e => e.approvalStatus === 'pending')
                .map(e => ({ ...e, _kind: 'flexday' }));
            const pendingOvertimes = (overtimes.status === 'fulfilled' ? overtimes.value : [])
                .filter(e => e.approvalStatus === 'pending')
                .map(e => ({ ...e, _kind: 'overtime', startDate: e.date, endDate: e.date, numberDays: null }));
            const all = [...pendingHolidays, ...pendingFlexDays, ...pendingOvertimes]
                .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
            setPendingItems(all);
            onCountChange?.(all.length + calls.length);
        } catch {
            // silent
        } finally {
            setLoading(false);
        }
    }, [onCountChange]);

    // Also while it stays open: an approval granted elsewhere should not need a reload.
    useLiveRefresh(load, { enabled: isOpen });

    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen, onClose]);

    const fmtMin = (m) => {
        const abs = Math.abs(m);
        return `${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
    };

    const kindLabel = (entry) => {
        if (entry._kind === 'flexday') return 'Flex Day';
        if (entry._kind === 'overtime') return 'Überstunden';
        return 'Urlaub';
    };

    const buildContext = (entry) => {
        const start = formatDate(entry.startDate);
        if (entry._kind === 'overtime') {
            return `${entry.displayName ?? 'Mitarbeiter'} (${start}, +${fmtMin(entry.overtimeMinutes)} h)`;
        }
        const end = formatDate(entry.endDate);
        const days = `${entry.numberDays} ${entry.numberDays === 1 ? 'Tag' : 'Tage'}`;
        return `${entry.displayName ?? 'Mitarbeiter'} (${start}–${end}, ${days})`;
    };

    const handleApprove = async (entry) => {
        const label = kindLabel(entry);
        if (!await confirm(`${label} für ${buildContext(entry)} genehmigen?`, { confirmLabel: 'Genehmigen' })) return;
        try {
            if (entry._kind === 'flexday') await approveFlexDay(entry.id);
            else if (entry._kind === 'overtime') await approveOvertimeApproval(entry.id);
            else await approveHoliday(entry.id);
            toast(`${label} genehmigt.`, 'success');
            await load();
        } catch {
            toast(`Fehler beim Genehmigen.`, 'error');
        }
    };

    const handleReject = async (entry) => {
        const label = kindLabel(entry);
        const reason = await prompt(`${label} für ${buildContext(entry)} ablehnen?`, {
            confirmLabel: 'Ablehnen',
            danger: true,
            placeholder: 'Grund für die Ablehnung (optional)',
        });
        if (reason === null) return; // cancelled
        const trimmed = reason || null;
        try {
            if (entry._kind === 'flexday') await rejectFlexDay(entry.id, trimmed);
            else if (entry._kind === 'overtime') await rejectOvertimeApproval(entry.id, trimmed);
            else await rejectHoliday(entry.id, trimmed);
            toast(`${label} abgelehnt.`, 'success');
            await load();
        } catch {
            toast(`Fehler beim Ablehnen.`, 'error');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="otm-modal" onClick={e => e.stopPropagation()}>
                <div className="otm-header">
                    <div className="otm-header-left">
                        <div className="otm-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                            </svg>
                        </div>
                        <div>
                            <h3 className="otm-title">Offene Aufgaben</h3>
                            {!loading && (
                                <p className="otm-sub">
                                    {pendingItems.length === 0 && callItems.length === 0
                                        ? 'Keine ausstehenden Aufgaben'
                                        : [
                                            callItems.length > 0 && `${callItems.length} Anruf${callItems.length === 1 ? '' : 'e'}`,
                                            pendingItems.length > 0 && `${pendingItems.length} Antrag${pendingItems.length === 1 ? '' : 'räge'}`,
                                        ].filter(Boolean).join(' · ')}
                                </p>
                            )}
                        </div>
                    </div>
                    <button className="otm-close" onClick={onClose} aria-label="Schließen">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>

                <div className="otm-body">
                    {/* Today's calls first — they are time-critical, approvals are not. */}
                    {!loading && callItems.length > 0 && (
                        <div className="otm-section">
                            <div className="otm-section-head">
                                ☎ Anrufen ({callItems.length})
                                <button
                                    className="otm-section-link"
                                    onClick={() => { onClose(); navigate('/project_acquisition'); }}
                                >
                                    Anrufliste öffnen
                                </button>
                            </div>
                            <ul className="otm-calls">
                                {callItems.slice(0, 5).map(item => (
                                    <li key={item.id} className="otm-call">
                                        <button
                                            className="otm-call-main"
                                            onClick={() => { onClose(); navigate(`/project_acquisition?deal=${item.id}`); }}
                                        >
                                            <span className="otm-call-company">{item.company_name}</span>
                                            <span className="otm-call-meta">
                                                {item.contact_person || '—'}
                                                <span className={`otm-call-flag is-${item.bucket}`}>{callNote(item)}</span>
                                            </span>
                                        </button>
                                        {item.contact_phone && (
                                            <a className="otm-call-phone" href={`tel:${item.contact_phone}`}>
                                                📞 {item.contact_phone}
                                            </a>
                                        )}
                                    </li>
                                ))}
                                {callItems.length > 5 && (
                                    <li className="otm-call otm-call-more">
                                        +{callItems.length - 5} weitere in der Anrufliste
                                    </li>
                                )}
                            </ul>
                        </div>
                    )}

                    {loading ? (
                        <p className="otm-empty">Laden…</p>
                    ) : pendingItems.length === 0 ? (
                        callItems.length === 0 && (
                            <div className="otm-all-done">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12"/>
                                </svg>
                                <p>Alles erledigt</p>
                            </div>
                        )
                    ) : (
                        <div className="table-scroll">
                            <table className="holiday-table">
                                <thead>
                                    <tr>
                                        <th>Typ</th>
                                        <th>Mitarbeiter</th>
                                        <th>Von</th>
                                        <th>Bis</th>
                                        <th>Dauer</th>
                                        <th>Aktion</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pendingItems.map(e => (
                                        <tr key={e.id}>
                                            <td>
                                                {e._kind === 'flexday'
                                                    ? <span className="otm-type-badge otm-type-flexday">Flex Day</span>
                                                    : e._kind === 'overtime'
                                                    ? <span className="otm-type-badge otm-type-overtime">Überstunden</span>
                                                    : <span className="otm-type-badge otm-type-holiday">Urlaubsantrag</span>}
                                            </td>
                                            <td>{e.displayName ?? '—'}</td>
                                            <td>{formatDate(e.startDate)}</td>
                                            <td>{e._kind === 'overtime' ? '—' : formatDate(e.endDate)}</td>
                                            <td>{e._kind === 'overtime' ? `+${fmtMin(e.overtimeMinutes)} h` : `${e.numberDays} ${e.numberDays === 1 ? 'Tag' : 'Tage'}`}</td>
                                            <td>
                                                <div className="otm-actions">
                                                    <button className="button-success" onClick={() => handleApprove(e)}>
                                                        Genehmigen
                                                    </button>
                                                    <button className="button-danger" onClick={() => handleReject(e)}>
                                                        Ablehnen
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
